import { mapPackById, ECUADOR_MAP_PACKS } from "../../shared/ecuador-map-packs";
import { getStoredMapPack } from "./offline-maps";
import type { GazetteerTuple } from "./place-search-core";
import { extractMapLabelFeaturesFromMvt, type NamedMapFeature } from "./map-label-extractor";
import { mapIndexProgress, mergeMapLabelTuples } from "./map-label-index-core";

const DATABASE = "thera-map-label-search";
const STORE = "indexes";
const DATABASE_VERSION = 1;
const INDEX_VERSION = 3;

export type MapLabelIndexStatus = "queued" | "building" | "ready" | "error";

export type MapLabelIndexRecord = {
  file: string;
  mapPackId: string;
  sourceSize: number;
  sourceDownloadedAt: string;
  indexVersion: number;
  generatedAt: string | null;
  updatedAt: string;
  status: MapLabelIndexStatus;
  zooms: number[];
  completedZooms: number[];
  currentZoom: number | null;
  processedTiles: number;
  totalTiles: number;
  progress: number;
  places: GazetteerTuple[];
  lastError?: string;
};

type QueueTask = {
  mapPackId: string;
  file: string;
  blob: Blob;
  downloadedAt: string;
  priority: boolean;
};

type WorkerOutput =
  | { type: "stage"; zooms: number[]; currentZoom: number; processedTiles: number; totalTiles: number }
  | { type: "features"; currentZoom: number; processedTiles: number; totalTiles: number; items: NamedMapFeature[] }
  | { type: "checkpoint"; zooms: number[]; completedZooms: number[]; currentZoom: number | null; processedTiles: number; totalTiles: number }
  | { type: "done"; zooms: number[]; completedZooms: number[] }
  | { type: "error"; message: string };

const queue: QueueTask[] = [];
const queuedFiles = new Set<string>();
let activeFile: string | null = null;
let activeWorker: Worker | null = null;
let activeFinish: (() => void) | null = null;
const liveProgress = new Map<string, number>();

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE, { keyPath: "file" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getStoredIndex(file: string) {
  const database = await openDatabase();
  try {
    return await new Promise<MapLabelIndexRecord | undefined>((resolve, reject) => {
      const request = database.transaction(STORE, "readonly").objectStore(STORE).get(file);
      request.onsuccess = () => resolve(request.result as MapLabelIndexRecord | undefined);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

async function saveStoredIndex(record: MapLabelIndexRecord) {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).put(record);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

function freshRecord(task: QueueTask): MapLabelIndexRecord {
  const now = new Date().toISOString();
  return {
    file: task.file,
    mapPackId: task.mapPackId,
    sourceSize: task.blob.size,
    sourceDownloadedAt: task.downloadedAt,
    indexVersion: INDEX_VERSION,
    generatedAt: null,
    updatedAt: now,
    status: "queued",
    zooms: [],
    completedZooms: [],
    currentZoom: null,
    processedTiles: 0,
    totalTiles: 0,
    progress: 0,
    places: [],
  };
}

function sameSource(record: MapLabelIndexRecord | undefined, task: QueueTask) {
  return Boolean(record
    && record.indexVersion === INDEX_VERSION
    && record.mapPackId === task.mapPackId
    && record.sourceSize === task.blob.size
    && record.sourceDownloadedAt === task.downloadedAt);
}

function queueTask(task: QueueTask) {
  if (activeFile === task.file) return;
  const existingIndex = queue.findIndex((item) => item.file === task.file);
  if (existingIndex >= 0) {
    if (task.priority && existingIndex > 0) {
      const [existing] = queue.splice(existingIndex, 1);
      queue.unshift({ ...existing, priority: true });
    }
    return;
  }
  if (task.priority) queue.unshift(task);
  else queue.push(task);
  queuedFiles.add(task.file);
  void pumpQueue();
}

async function runWorkerBuild(task: QueueTask) {
  let record = await getStoredIndex(task.file).catch(() => undefined);
  if (!sameSource(record, task)) {
    record = freshRecord(task);
    await saveStoredIndex(record);
  }
  if (!record) return;
  if (record.status === "ready") return;

  if (typeof Worker === "undefined") {
    record.status = "error";
    record.lastError = "Este dispositivo no permite preparar el índice offline en segundo plano.";
    record.updatedAt = new Date().toISOString();
    await saveStoredIndex(record);
    return;
  }

  record.status = "building";
  record.lastError = undefined;
  record.updatedAt = new Date().toISOString();
  await saveStoredIndex(record);

  const worker = new Worker(new URL("./android-map-label-index.worker.ts", import.meta.url), { type: "module" });
  activeWorker = worker;
  const tupleMap = new Map(record.places.map((tuple) => [String(tuple[0]), tuple]));

  await new Promise<void>((resolve) => {
    activeFinish = resolve;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      activeFinish = null;
      worker.terminate();
      if (activeWorker === worker) activeWorker = null;
      resolve();
    };

    const mergeItems = (items: NamedMapFeature[]) => {
      for (const item of items) {
        const key = String(item.tuple[0]);
        const current = tupleMap.get(key);
        if (!current || Number(item.tuple[6] ?? 0) > Number(current[6] ?? 0)) tupleMap.set(key, item.tuple);
      }
      record!.places = [...tupleMap.values()].sort((left, right) => Number(right[6] ?? 0) - Number(left[6] ?? 0));
    };

    const persist = async () => {
      record!.updatedAt = new Date().toISOString();
      await saveStoredIndex(record!).catch(() => undefined);
    };

    worker.onmessage = (event: MessageEvent<WorkerOutput>) => {
      const message = event.data;
      if (message.type === "features") {
        mergeItems(message.items);
        record!.currentZoom = message.currentZoom;
        record!.processedTiles = message.processedTiles;
        record!.totalTiles = message.totalTiles;
        record!.progress = mapIndexProgress(
          record!.zooms,
          record!.completedZooms,
          record!.currentZoom,
          record!.processedTiles,
          record!.totalTiles,
        );
        liveProgress.set(task.file, record!.progress);
        return;
      }

      if (message.type === "stage") {
        record!.zooms = message.zooms;
        record!.currentZoom = message.currentZoom;
        record!.processedTiles = message.processedTiles;
        record!.totalTiles = message.totalTiles;
        record!.status = "building";
        record!.progress = mapIndexProgress(
          record!.zooms,
          record!.completedZooms,
          record!.currentZoom,
          record!.processedTiles,
          record!.totalTiles,
        );
        liveProgress.set(task.file, record!.progress);
        void persist();
        return;
      }

      if (message.type === "checkpoint") {
        record!.zooms = message.zooms;
        record!.completedZooms = message.completedZooms;
        record!.currentZoom = message.currentZoom;
        record!.processedTiles = message.processedTiles;
        record!.totalTiles = message.totalTiles;
        record!.progress = mapIndexProgress(
          record!.zooms,
          record!.completedZooms,
          record!.currentZoom,
          record!.processedTiles,
          record!.totalTiles,
        );
        liveProgress.set(task.file, record!.progress);
        void persist();
        return;
      }

      if (message.type === "done") {
        record!.zooms = message.zooms;
        record!.completedZooms = message.completedZooms;
        record!.currentZoom = null;
        record!.processedTiles = 0;
        record!.totalTiles = 0;
        record!.progress = 100;
        record!.status = "ready";
        record!.generatedAt = new Date().toISOString();
        record!.updatedAt = record!.generatedAt;
        liveProgress.set(task.file, 100);
        void saveStoredIndex(record!).finally(finish);
        return;
      }

      if (message.type === "error") {
        record!.status = "error";
        record!.lastError = message.message;
        record!.updatedAt = new Date().toISOString();
        void saveStoredIndex(record!).finally(finish);
      }
    };

    worker.onerror = (event) => {
      record!.status = "error";
      record!.lastError = event.message || "No fue posible preparar la búsqueda offline del mapa.";
      record!.updatedAt = new Date().toISOString();
      void saveStoredIndex(record!).finally(finish);
    };

    worker.postMessage({
      type: "build",
      mapPackId: task.mapPackId,
      file: task.file,
      blob: task.blob,
      completedZooms: record.completedZooms,
      currentZoom: record.currentZoom,
      processedTiles: record.processedTiles,
    });
  });
}

async function pumpQueue() {
  if (activeFile) return;
  const task = queue.shift();
  if (!task) return;
  queuedFiles.delete(task.file);
  activeFile = task.file;
  try {
    await runWorkerBuild(task);
  } finally {
    liveProgress.delete(task.file);
    activeFile = null;
    void pumpQueue();
  }
}

export async function scheduleMapLabelIndexForPack(mapPackId: string, priority = false) {
  const pack = mapPackById(mapPackId);
  if (!pack || pack.overview) return null;
  const stored = await getStoredMapPack(pack.file).catch(() => undefined);
  if (!stored?.blob?.size) return null;
  const task: QueueTask = {
    mapPackId: pack.id,
    file: pack.file,
    blob: stored.blob,
    downloadedAt: stored.downloadedAt,
    priority,
  };
  const existing = await getStoredIndex(pack.file).catch(() => undefined);
  if (sameSource(existing, task) && existing?.status === "ready") return existing;
  if (!sameSource(existing, task)) await saveStoredIndex(freshRecord(task)).catch(() => undefined);
  queueTask(task);
  return existing ?? null;
}

export async function scheduleDownloadedMapLabelIndexes(preferredPackId?: string | null) {
  const packs = ECUADOR_MAP_PACKS.filter((pack) => !pack.overview);
  const ordered = preferredPackId
    ? [...packs].sort((left, right) => Number(right.id === preferredPackId) - Number(left.id === preferredPackId))
    : packs;
  for (const pack of ordered) {
    await scheduleMapLabelIndexForPack(pack.id, pack.id === preferredPackId).catch(() => null);
  }
}

export async function getMapLabelIndexSummary(mapPackIds?: Set<string> | null) {
  const database = await openDatabase();
  try {
    const records = await new Promise<MapLabelIndexRecord[]>((resolve, reject) => {
      const request = database.transaction(STORE, "readonly").objectStore(STORE).getAll();
      request.onsuccess = () => resolve((request.result ?? []) as MapLabelIndexRecord[]);
      request.onerror = () => reject(request.error);
    });
    const relevant = records.filter((record) => record.indexVersion === INDEX_VERSION)
      .filter((record) => !mapPackIds || mapPackIds.has(record.mapPackId));
    const building = relevant.filter((record) => record.status === "building" || record.status === "queued");
    const ready = relevant.filter((record) => record.status === "ready");
    const progressValues = building.map((record) => liveProgress.get(record.file) ?? record.progress ?? 0);
    return {
      building: building.length > 0,
      readyCount: ready.length,
      indexedCount: relevant.length,
      progress: progressValues.length ? Math.round(progressValues.reduce((sum, value) => sum + value, 0) / progressValues.length) : (ready.length ? 100 : 0),
      mapPackIds: relevant.map((record) => record.mapPackId),
    };
  } finally {
    database.close();
  }
}

export async function removeMapLabelIndex(file: string) {
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    if (queue[index].file === file) queue.splice(index, 1);
  }
  queuedFiles.delete(file);
  liveProgress.delete(file);

  if (activeFile === file && activeWorker) {
    activeWorker.postMessage({ type: "cancel" });
    activeWorker.terminate();
    activeWorker = null;
    activeFinish?.();
    activeFinish = null;
  }

  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).delete(file);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

export async function loadMapLabelTuples(mapPackIds?: Set<string> | null) {
  const database = await openDatabase();
  try {
    const records = await new Promise<MapLabelIndexRecord[]>((resolve, reject) => {
      const request = database.transaction(STORE, "readonly").objectStore(STORE).getAll();
      request.onsuccess = () => resolve((request.result ?? []) as MapLabelIndexRecord[]);
      request.onerror = () => reject(request.error);
    });
    const tuples = records
      .filter((record) => record.indexVersion === INDEX_VERSION)
      .filter((record) => record.status === "building" || record.status === "ready" || record.status === "queued")
      .filter((record) => !mapPackIds || mapPackIds.has(record.mapPackId))
      .flatMap((record) => record.places ?? []);
    return mergeMapLabelTuples([], tuples);
  } finally {
    database.close();
  }
}

// Compatibilidad con las pruebas y utilidades existentes de V3.
export { extractMapLabelFeaturesFromMvt };
